package dev.lingvo.site.pages.app.projects.slug.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.ProjectProviderMode
import dev.lingvo.shared.ProviderReadiness
import dev.lingvo.shared.ProviderSettingsDto
import dev.lingvo.shared.creditCost
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBotIcon
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LIconBadge
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LPresencePhase
import dev.lingvo.site.ds.LPresenceState
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionSurface
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.pages.app.projects.slug.SettingsDraftProtection
import dev.lingvo.site.pages.app.settings.providerReadinessBadge
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

@Composable
internal fun ProjectProviderDetail(
    settings: ProviderSettingsDto,
    slug: String,
    presence: LPresenceState,
    onClose: () -> Unit,
    onDirtyChanged: (Boolean) -> Unit,
    focusOnEnter: Boolean,
    onChanged: () -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
    updateProvider: suspend (String, MtProvider, ProjectProviderUpdateCommand) -> ProviderSettingsDto,
) {
    val scope = rememberCoroutineScope()
    var mode by remember(settings.provider, settings.updatedAt, settings.mode) { mutableStateOf(settings.mode) }
    var apiKey by remember(settings.provider, settings.updatedAt) { mutableStateOf("") }
    var model by remember(settings.provider, settings.updatedAt) { mutableStateOf(settings.model.orEmpty()) }
    var baseUrl by remember(settings.provider, settings.updatedAt) { mutableStateOf(settings.baseUrl.orEmpty()) }
    var saving by remember(settings.provider) { mutableStateOf(false) }
    var testing by remember(settings.provider) { mutableStateOf(false) }
    var clearing by remember(settings.provider) { mutableStateOf(false) }
    var confirmClear by remember(settings.provider) { mutableStateOf(false) }
    var clearError by remember(settings.provider) { mutableStateOf<String?>(null) }
    var error by remember(settings.provider) { mutableStateOf<String?>(null) }
    var testResult by remember(settings.provider) { mutableStateOf<Pair<Boolean, String>?>(null) }
    val palette = ColorMode.current.toSitePalette()
    val presentation = providerPresentation(settings)
    val needsKey = mode == ProjectProviderMode.OVERRIDE && settings.requiresKey
    val hasUsableProjectKey = settings.hasUsableStoredProjectKey()
    val dirty = providerDraftDirty(settings, mode, apiKey, model, baseUrl)
    val exiting = presence.phase == LPresencePhase.EXITING
    val endpointNeedsCredentialConfirmation = libreEndpointCredentialConfirmationRequired(
        settings = settings,
        baseUrl = baseUrl,
        apiKey = apiKey,
    )
    val latestOnDirtyChanged by rememberUpdatedState(onDirtyChanged)

    fun discardDraft() {
        mode = settings.mode
        apiKey = ""
        model = settings.model.orEmpty()
        baseUrl = settings.baseUrl.orEmpty()
        error = null
        testResult = null
        latestOnDirtyChanged(false)
    }

    LaunchedEffect(dirty) {
        latestOnDirtyChanged(dirty)
    }
    // The card itself takes focus rather than its first field: a screen reader hears the provider
    // and its current mode before being dropped into an input, and Tab still reaches the form.
    LaunchedEffect(settings.provider, presence.phase, focusOnEnter) {
        if (!focusOnEnter || presence.phase != LPresencePhase.PRESENT) return@LaunchedEffect
        (document.getElementById(providerDetailId(settings.provider.id)) as? HTMLElement)?.focus()
    }
    DisposableEffect(settings.provider) {
        onDispose { latestOnDirtyChanged(false) }
    }
    SettingsDraftProtection(
        dirty = dirty,
        guardId = "project-provider-${settings.provider.id}",
        onDiscard = ::discardDraft,
    )

    LCard(
        Modifier
            .fillMaxWidth()
            .attr("id", providerDetailId(settings.provider.id))
            .attr("data-provider-detail", settings.provider.id)
            .attr("data-presence", presence.phase.attributeValue)
            .attr("tabindex", "-1")
            .then(if (exiting) Modifier.attr("inert", "").attr("aria-hidden", "true") else Modifier)
            .styleModifier {
                val visible = presence.phase == LPresencePhase.PRESENT
                property("opacity", if (visible) "1" else "0")
                property("transform", if (visible) "none" else "translateY(-${Motion.PopoverTravelPx}px)")
                if (exiting) property("pointer-events", "none")
            }
            .transitionSurface(
                durationMs = if (exiting) Motion.FastMs else Motion.StandardMs,
                easing = if (exiting) Motion.EaseIn else Motion.EaseStrongOut,
            ),
    ) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            Row(
                Modifier.fillMaxWidth().gap(Space.MD).styleModifier { property("flex-wrap", "wrap") },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                LIconBadge { LBotIcon(Modifier.iconSize(IconSize.XXL)) }
                Column(Modifier.minWidth(0.px).gap(Space.XXS).styleModifier { property("flex", "1 1 220px") }) {
                    H3(Modifier.fontSize(TypeScale.HeadingSmall).fontWeight(700).margin(0.px).toAttrs()) {
                        Text(tf("app.projects.slug.settings.project_provider_detail.configure_provider", "Configure {provider}", "provider" to settings.provider.displayName))
                    }
                    Span(Modifier.color(palette.textSecondary).fontSize(TypeScale.Small).toAttrs()) {
                        Text(presentation.summary)
                    }
                }
                LBadge(presentation.readinessLabel, variant = providerReadinessBadge(settings.readiness))
            }

            settings.unavailableReason?.let { reason ->
                LAlert(t("app.projects.slug.settings.project_provider_detail.provider_unavailable", "Provider unavailable"), reason, variant = LAlertVariant.WARNING)
            }
            if (settings.keyUnreadable) {
                LAlert(
                    t("app.projects.slug.settings.project_provider_detail.this_project_key_cant_be_read", "This project key can't be read"),
                    tf(
                        "app.projects.slug.settings.project_provider_detail.enter_it_again_before_using_provider",
                        "Enter it again before using {provider}.",
                        "provider" to settings.provider.displayName,
                    ),
                    variant = LAlertVariant.WARNING,
                )
            }

            ProviderEditor(
                settings = settings,
                mode = mode,
                apiKey = apiKey,
                model = model,
                baseUrl = baseUrl,
                enabled = !saving && !testing && !clearing,
                needsModel = settings.provider == MtProvider.CLAUDE,
                needsBaseUrl = settings.provider == MtProvider.LIBRETRANSLATE,
                onMode = { mode = it; error = null; testResult = null },
                onApiKey = { apiKey = it; error = null; testResult = null },
                onModel = { model = it; error = null; testResult = null },
                onBaseUrl = { baseUrl = it; error = null; testResult = null },
            )

            if (endpointNeedsCredentialConfirmation) {
                LAlert(
                    title = t("app.projects.slug.settings.project_provider_detail.confirm_the_key_for_this_server", "Confirm the key for this server"),
                    description = t("app.projects.slug.settings.project_provider_detail.enter_the_optional_api_key_again_2", "Enter the optional API key again or clear the stored project key before " +
                        "changing the LibreTranslate Base URL. Lingvo never forwards a write-only key to a new server."),
                    variant = LAlertVariant.WARNING,
                )
            }

            error?.let { LAlert(t("app.projects.slug.settings.project_provider_detail.couldnt_save_provider_settings", "Couldn't save provider settings"), it, variant = LAlertVariant.DANGER, live = true) }
            testResult?.let { result ->
                LAlert(
                    if (result.first) t("app.projects.slug.settings.project_provider_detail.connection_works", "Connection works") else t("app.projects.slug.settings.project_provider_detail.connection_failed", "Connection failed"),
                    result.second,
                    variant = if (result.first) LAlertVariant.SUCCESS else LAlertVariant.DANGER,
                    live = true,
                )
            }

            Span(Modifier.color(palette.textMuted).fontSize(TypeScale.Micro).toAttrs()) {
                Text(presentation.testCostNote)
            }
            Row(
                Modifier
                    .fillMaxWidth()
                    .gap(Space.SM)
                    .styleModifier { property("flex-wrap", "wrap") }
                    .attr("data-provider-actions", ""),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                LButton(
                    t("app.projects.slug.settings.project_provider_detail.close", "Close"),
                    onClose,
                    providerActionModifier(),
                    variant = LButtonVariant.GHOST,
                    enabled = !saving && !testing && !clearing,
                )
                if (settings.hasStoredProjectKey || settings.hasKey || settings.keyUnreadable) {
                    LButton(
                        t("app.projects.slug.settings.project_provider_detail.clear_stored_project_key", "Clear stored project key"),
                        {
                            clearError = null
                            confirmClear = true
                        },
                        providerActionModifier(),
                        variant = LButtonVariant.GHOST,
                        enabled = !saving && !testing && !clearing,
                    )
                }
                LButton(
                    label = t("app.projects.slug.settings.project_provider_detail.test_connection", "Test connection"),
                    onClick = {
                        scope.launch {
                            testing = true
                            try {
                                testResult = settingsOperationResult {
                                    ApiClient.testProvider(slug, settings.provider)
                                }.fold(
                                    { result -> result.ok to buildString {
                                        append(result.message)
                                        result.sample?.let { append(" Sample: $it") }
                                        if (result.consumedLingvoCredit) {
                                            append(" ")
                                            append(
                                                tPlural(
                                                    "app.projects.slug.settings.project_provider_detail.count.credit_used",
                                                    creditCost(settings.provider),
                                                    "{count} Lingvo credit was used.",
                                                    "{count} Lingvo credits were used.",
                                                ),
                                            )
                                        }
                                    } },
                                    { false to (it.message ?: tr(
                                        "app.projects.slug.settings.project_provider_detail.check_your_connection",
                                        "Check your connection and retry.",
                                    )) },
                                )
                            } finally {
                                testing = false
                            }
                        }
                    },
                    modifier = providerActionModifier(),
                    variant = LButtonVariant.SECONDARY,
                    enabled = !dirty && settings.readiness == ProviderReadiness.READY && !saving && !clearing,
                    loading = testing,
                )
                LButton(
                    label = t("app.projects.slug.settings.project_provider_detail.save_provider", "Save provider"),
                    onClick = {
                        when {
                            needsKey && apiKey.isBlank() && !hasUsableProjectKey -> {
                                error = trf(
                                    "app.projects.slug.settings.project_provider_detail.enter_this_projects_api_key",
                                    "Enter this project's {provider} API key.",
                                    "provider" to settings.provider.displayName,
                                )
                            }
                            endpointNeedsCredentialConfirmation -> {
                                error = tr(
                                    "app.projects.slug.settings.project_provider_detail.stored_key_different_server",
                                    "A stored key cannot be sent to a different server. Enter the optional API " +
                                        "key again, or clear the stored project key before changing the Base URL.",
                                )
                            }
                            else -> {
                            scope.launch {
                                saving = true
                                error = null
                                try {
                                    settingsOperationResult {
                                        updateProvider(
                                            slug,
                                            settings.provider,
                                            providerUpdateRequest(
                                                provider = settings.provider,
                                                mode = mode,
                                                apiKey = apiKey,
                                                model = model,
                                                baseUrl = baseUrl,
                                                storedModel = settings.model,
                                                storedBaseUrl = settings.baseUrl,
                                            ),
                                        )
                                    }.onSuccess {
                                        apiKey = ""
                                        notify(
                                            trf(
                                                "app.projects.slug.settings.project_provider_detail.provider_settings_saved",
                                                "{provider} settings saved",
                                                "provider" to settings.provider.displayName,
                                            ),
                                            LToastVariant.SUCCESS,
                                            4_000,
                                        )
                                        onChanged()
                                    }.onFailure {
                                        error = it.message ?: tr(
                                            "app.projects.slug.settings.project_provider_detail.check_your_connection",
                                            "Check your connection and retry.",
                                        )
                                    }
                                } finally {
                                    saving = false
                                }
                            }
                            }
                        }
                    },
                    modifier = providerActionModifier(),
                    enabled = dirty && !testing && !saving && !clearing,
                    loading = saving,
                )
            }
        }
    }

    LModal(
        open = confirmClear,
        title = t("app.projects.slug.settings.project_provider_detail.clear_key_and_follow_workspace_policy", "Clear key and follow workspace policy?"),
        onClose = { if (!clearing) confirmClear = false },
        actions = {
            LButton(t("app.projects.slug.settings.project_provider_detail.keep_key", "Keep key"), { confirmClear = false }, variant = LButtonVariant.GHOST, enabled = !clearing)
            LButton(
                label = t("app.projects.slug.settings.project_provider_detail.clear_key_and_follow_policy", "Clear key and follow policy"),
                onClick = {
                    scope.launch {
                        clearing = true
                        clearError = null
                        try {
                            settingsOperationResult {
                                updateProvider(slug, settings.provider, clearStoredProviderKeyRequest())
                            }.onSuccess {
                                apiKey = ""
                                confirmClear = false
                                notify(
                                    trf(
                                        "app.projects.slug.settings.project_provider_detail.provider_project_key_cleared",
                                        "{provider} project key cleared",
                                        "provider" to settings.provider.displayName,
                                    ),
                                    LToastVariant.SUCCESS,
                                    4_000,
                                )
                                onChanged()
                            }.onFailure {
                                clearError = it.message ?: tr(
                                    "app.projects.slug.settings.project_provider_detail.check_your_connection",
                                    "Check your connection and retry.",
                                )
                            }
                        } finally {
                            clearing = false
                        }
                    }
                },
                variant = LButtonVariant.DANGER,
                enabled = !clearing,
                loading = clearing,
            )
        },
    ) {
        LAlert(
            t("app.projects.slug.settings.project_provider_detail.this_cannot_be_undone", "This cannot be undone"),
            tf(
                "app.projects.slug.settings.project_provider_detail.this_removes_the_stored_project_key",
                "This removes the stored project key and switches {provider} to inherited policy. " +
                    "The effective credential can be workspace, Lingvo-funded, or none. Workspace " +
                    "secrets are never shown or changed.",
                "provider" to settings.provider.displayName,
            ),
            variant = LAlertVariant.DANGER,
        )
        clearError?.let {
            LAlert(t("app.projects.slug.settings.project_provider_detail.couldnt_clear_the_project_key", "Couldn't clear the project key"), it, variant = LAlertVariant.DANGER, live = true)
        }
    }
}

private fun providerActionModifier(): Modifier = Modifier.styleModifier {
    property("flex", "1 1 160px")
    property("min-width", "min(100%, 140px)")
}
